import { Component, DestroyRef, inject, OnDestroy, OnInit, Output, EventEmitter, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { GlobalValuesService } from '@app/core';
import { DrawerEscapeBlockerService, ScreenService, OpportunityService, CompanyService, ProductsService } from '@app/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, combineLatest, startWith, of, switchMap, map, Observable } from 'rxjs';
import { isProductInCompanyScope, Partner, Product, SaleOrder, SaleOrderLineOptions, ToastConfig } from '@app/shared';
import { ModifyPartnerDrawerComponent } from 'src/app/features/maestros/drawers/modify-partner-drawer/modify-partner-drawer.component';
import { NzNotificationRef, NzNotificationService } from 'ng-zorro-antd/notification';
import { resolveOfferedTaxes } from '../tax-catalog-lookup.util';
import { resolveLineWriteErrorMessage } from '../../components/budget-content/budget-content-line-error.util';

@Component({
  selector: 'app-create-budget-line-drawer',
  standalone: false,
  templateUrl: './create-budget-line-drawer.component.html',
  styleUrl: './create-budget-line-drawer.component.less',
})
export class CreateBudgetLineDrawerComponent implements OnInit, OnDestroy {
  private static localBudgetLineIdSequence = 0;

  nzData: {
    budgetId?: string | number;
    budget?: SaleOrder;
    lineData?: any;
    budgetLines?: any[];
  } = inject(NZ_DRAWER_DATA);

  @Output() lineCreated = new EventEmitter<any>();
  @ViewChild('notificationTmpl') notificationTmpl?: TemplateRef<any>;
  ModifyPartnerDrawerComponent = ModifyPartnerDrawerComponent;

  budgetLineForm: FormGroup;
  costLineForm: FormGroup;
  saleLineForm: FormGroup;

  // Loading states
  saving: boolean = false;
  createAssociatedCost: boolean = false;
  createAssociatedSale: boolean = false;
  currentStep: 'sale' | 'cost' | 'associatedSale' = 'sale';

  // Data lists
  productList: Product[] = [];
  filteredProducts: Product[] = []; // Productos filtrados según isCost
  costFilteredProducts: Product[] = [];
  saleFilteredProducts: Product[] = [];
  supplierList: any[] = [];
  unitList: any[] = [];
  budgetUnitList: any[] = [];
  costUnitList: any[] = [];
  saleUnitList: any[] = [];
  currencies: any[] = [];
  companyTaxes: any[] = [];
  filteredTaxes: any[] = [];
  costFilteredTaxes: any[] = [];
  // CEC-687: catálogo de impuestos de TODAS las compañías, para poder enseñar el que ya tiene la línea.
  private taxCatalog: any[] = [];
  locationList: any[] = [];
  notRef?: NzNotificationRef;
  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: '',
    showUndo: false,
  };

  associatedSaleLines: any[] = [];

  // Flag para controlar el autocompletado durante la inicialización
  private isInitializingForm: boolean = false;
  private autoPrefilledCostProductId: number | null = null;
  private autoPrefilledSaleProductId: number | null = null;
  private lineOptions?: SaleOrderLineOptions;

  private destroyRef = inject(DestroyRef);
  private opportunityService = inject(OpportunityService);
  private companyService = inject(CompanyService);
  private productsService = inject(ProductsService);
  private notificationService = inject(NzNotificationService);

  constructor(
    private drawerRef: NzDrawerRef<string>,
    private globalValuesService: GlobalValuesService,
    public screenService: ScreenService,
    private fb: FormBuilder,
    private drawerEscapeBlocker: DrawerEscapeBlockerService,
  ) {
    this.budgetLineForm = this.fb.group({
      name: ['', Validators.required],
      isCost: [false],
      isUnitPrice: [true],
      productId: [null, this.productRequiredValidator],
      supplierId: [0],
      quantity: [0, [Validators.required, Validators.min(0)]],
      unitOfMeasureId: [0],
      price: [0, [Validators.required, Validators.min(0)]],
      discount: [0, [Validators.min(0), Validators.max(100)]],
      currencyId: [this.getBudgetCurrencyId(), Validators.required],
      amount: [{ value: 0, disabled: true }],
      taxIds: [null], // Single selection now
      associatedSaleLineId: [null],
      sourceId: [null],
      destinationId: [null],
    });

    this.costLineForm = this.fb.group({
      name: ['', Validators.required],
      isCost: [true],
      isUnitPrice: [true],
      productId: [null, this.productRequiredValidator],
      supplierId: [0],
      quantity: [0, [Validators.required, Validators.min(0)]],
      unitOfMeasureId: [0],
      price: [0, [Validators.required, Validators.min(0)]],
      discount: [0, [Validators.min(0), Validators.max(100)]],
      currencyId: [this.nzData.budget?.currencyId || 1, Validators.required],
      amount: [{ value: 0, disabled: true }],
      taxIds: [null],
      associatedSaleLineId: [{ value: null, disabled: true }],
      sourceId: [null],
      destinationId: [null],
    });

    this.saleLineForm = this.fb.group({
      name: ['', Validators.required],
      isCost: [false],
      isUnitPrice: [true],
      productId: [null, this.productRequiredValidator],
      supplierId: [0],
      quantity: [0, [Validators.required, Validators.min(0)]],
      unitOfMeasureId: [0],
      price: [0, [Validators.required, Validators.min(0)]],
      discount: [0, [Validators.min(0), Validators.max(100)]],
      currencyId: [this.nzData.budget?.currencyId || 1, Validators.required],
      amount: [{ value: 0, disabled: true }],
      taxIds: [null],
      associatedSaleLineId: [{ value: null, disabled: true }],
      sourceId: [null],
      destinationId: [null],
    });

    // Suscribirse a cambios para calcular el importe
    this.budgetLineForm
      .get('quantity')
      ?.valueChanges.subscribe(() => this.calculateAmount());
    this.budgetLineForm
      .get('price')
      ?.valueChanges.subscribe(() => this.calculateAmount());
    this.budgetLineForm
      .get('discount')
      ?.valueChanges.subscribe(() => this.calculateAmount());
    this.budgetLineForm
      .get('isUnitPrice')
      ?.valueChanges.subscribe(() => this.calculateAmount());
    this.budgetLineForm
      .get('currencyId')
      ?.valueChanges.subscribe(() => this.calculateAmount());
    this.budgetLineForm
      .get('associatedSaleLineId')
      ?.valueChanges.subscribe(() => this.syncCostCurrencyFromAssociatedSale());

    this.costLineForm
      .get('quantity')
      ?.valueChanges.subscribe(() => this.calculateCostAmount());
    this.costLineForm
      .get('price')
      ?.valueChanges.subscribe(() => this.calculateCostAmount());
    this.costLineForm
      .get('discount')
      ?.valueChanges.subscribe(() => this.calculateCostAmount());
    this.costLineForm
      .get('isUnitPrice')
      ?.valueChanges.subscribe(() => {
        this.calculateCostAmount();
        this.updateFilteredCostTaxes();
      });
    this.costLineForm
      .get('currencyId')
      ?.valueChanges.subscribe(() => this.calculateCostAmount());

    this.saleLineForm
      .get('quantity')
      ?.valueChanges.subscribe(() => this.calculateSaleAmount());
    this.saleLineForm
      .get('price')
      ?.valueChanges.subscribe(() => this.calculateSaleAmount());
    this.saleLineForm
      .get('discount')
      ?.valueChanges.subscribe(() => this.calculateSaleAmount());
    this.saleLineForm
      .get('isUnitPrice')
      ?.valueChanges.subscribe(() => this.calculateSaleAmount());
    this.saleLineForm
      .get('currencyId')
      ?.valueChanges.subscribe(() => this.calculateSaleAmount());

    // Suscribirse a cambios de producto para autocompletar campos
    this.budgetLineForm
      .get('productId')
      ?.valueChanges.subscribe((productId) => {
        this.onProductChange(productId);
        this.updateFilteredTaxes();
      });

    this.costLineForm
      .get('productId')
      ?.valueChanges.subscribe((productId) => {
        this.onCostProductChange(productId);
        this.updateFilteredCostTaxes();
      });

    this.saleLineForm
      .get('productId')
      ?.valueChanges.subscribe((productId) => {
        this.onAssociatedSaleProductChange(productId);
      });

    // Suscribirse a cambios de isCost para actualizar taxes
    this.budgetLineForm.get('isCost')?.valueChanges.subscribe(() => {
      this.updateFilteredTaxes();
      this.updateValidators();

      const isCost = this.budgetLineForm.get('isCost')?.value;
      const isUnitPrice = this.budgetLineForm.get('isUnitPrice')?.value;
      if (isUnitPrice) {
        this.budgetLineForm.patchValue({ taxIds: null }, { emitEvent: false });
      }

      if (isCost) {
        this.createAssociatedCost = false;
        this.createAssociatedSale = false;
        this.currentStep = 'sale';
      }
    });

    this.budgetLineForm.get('isUnitPrice')?.valueChanges.subscribe(() => {
      this.updateFilteredTaxes();
      const isUnitPrice = this.budgetLineForm.get('isUnitPrice')?.value;
      if (isUnitPrice) {
        this.budgetLineForm.patchValue({ taxIds: null }, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    // Cargar listas de referencia
    this.loadReferenceData();

    // Si hay datos de línea (modo edición), inicializar formulario
    if (this.nzData.lineData) {
      this.initFormWithData(this.nzData.lineData);
    }

    this.updateAssociatedSaleLines();
    this.updateValidators();
    this.calculateAmount();

    // Registrar handler nativo en fase de captura para interceptar Escape
    this.drawerEscapeBlocker.block();
  }

  get isUnitPriceSegmented(): string {
    return this.budgetLineForm.get('isUnitPrice')?.value ? 'Unitario' : 'Total';
  }

  get isCostSegmented(): string {
    return this.budgetLineForm.get('isCost')?.value ? 'Coste' : 'Venta';
  }

  onUnitPriceSegmentedChange(value: any): void {
    this.budgetLineForm.get('isUnitPrice')?.setValue(value === 'Unitario');
  }

  onCostSegmentedChange(value: any): void {
    this.budgetLineForm.get('isCost')?.setValue(value === 'Coste');
  }

  get costIsUnitPriceSegmented(): string {
    return this.costLineForm.get('isUnitPrice')?.value ? 'Unitario' : 'Total';
  }

  get costIsCostSegmented(): string {
    return this.costLineForm.get('isCost')?.value ? 'Coste' : 'Venta';
  }

  get costProductOptions(): Product[] {
    return this.withSelectedProduct(
      this.costFilteredProducts,
      this.costLineForm.get('productId')?.value,
    );
  }

  get associatedSaleProductOptions(): Product[] {
    return this.withSelectedProduct(
      this.saleFilteredProducts,
      this.saleLineForm.get('productId')?.value,
    );
  }

  onCostUnitPriceSegmentedChange(value: any): void {
    this.costLineForm.get('isUnitPrice')?.setValue(value === 'Unitario');
  }

  get hasPersistedBudget(): boolean {
    return !!(this.nzData.budget?.id || this.nzData.budgetId);
  }

  private getSaleOrderId(): number | null {
    const saleOrderId = this.nzData.budget?.id || this.nzData.budgetId;
    return saleOrderId ? Number(saleOrderId) : null;
  }

  get canCreateAssociatedCost(): boolean {
    return (
      !this.budgetLineForm.get('isCost')?.value
    );
  }

  get showCostStep(): boolean {
    return this.canCreateAssociatedCost && this.createAssociatedCost;
  }

  get canCreateAssociatedSale(): boolean {
    return (
      this.budgetLineForm.get('isCost')?.value
    );
  }

  get showSaleStep(): boolean {
    return this.canCreateAssociatedSale && this.createAssociatedSale;
  }

  goToSaleStep(): void {
    this.createAssociatedCost = false;
    this.createAssociatedSale = false;
    this.currentStep = 'sale';
    this.updateValidators();
  }

  goToCostStep(): void {
    if (!this.canCreateAssociatedCost) {
      return;
    }

    if (this.budgetLineForm.invalid) {
      this.markFormDirty(this.budgetLineForm);
      return;
    }

    this.prefillCostFromSale();
    this.createAssociatedCost = true;
    this.currentStep = 'cost';
  }

  goToAssociatedSaleStep(): void {
    if (!this.canCreateAssociatedSale) {
      return;
    }

    this.createAssociatedSale = true;
    this.updateValidators();

    if (this.budgetLineForm.invalid) {
      this.markFormDirty(this.budgetLineForm);
      return;
    }

    this.prefillSaleFromCost();
    this.currentStep = 'associatedSale';
  }

  get isEditMode(): boolean {
    return !!this.nzData.lineData?.id;
  }

  // Validador personalizado para requerir que el producto no sea 0
  productRequiredValidator = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === 0) {
      return { required: true };
    }
    return null;
  };

  initFormWithData(lineData: any): void {
    // Activar bandera para prevenir autocompletado durante la inicialización
    this.isInitializingForm = true;

    // Preparar taxIds: convertir de array a valor simple si es necesario
    let taxId = null;
    if (lineData.taxIds && lineData.taxIds.length > 0) {
      taxId = lineData.taxIds[0];
    } else if (lineData.taxes && lineData.taxes.length > 0) {
      // Si taxes es un array de objetos
      taxId =
        typeof lineData.taxes[0] === 'object'
          ? lineData.taxes[0].id
          : lineData.taxes[0];
    }

    const productId =
      lineData.productId || lineData.product?.id || lineData.product || 0;

    this.budgetLineForm.patchValue({
      name: lineData.name || '',
      isCost: lineData.isCost || false,
      isUnitPrice:
        lineData.isUnitPrice !== undefined ? lineData.isUnitPrice : false,
      productId: productId,
      supplierId:
        lineData.supplierId || lineData.supplier?.id || lineData.supplier || 0,
      quantity: lineData.quantity || 0,
      unitOfMeasureId:
        lineData.unitOfMeasureId ||
        lineData.unitMeasure?.id ||
        lineData.unit ||
        0,
      price: lineData.price || 0,
      discount: lineData.discount || 0,
      currencyId: lineData.currencyId || lineData.currency?.id || lineData.currency || this.getBudgetCurrencyId(),
      taxIds: taxId,
      associatedSaleLineId:
        this.getIdValue(lineData.associatedSaleLineId) ||
        this.getIdValue(lineData.associatedSale) ||
        this.getIdValue(lineData.associatedSaleLine) ||
        null,
      sourceId: lineData.sourceId || null,
      destinationId: lineData.destinationId || null,
    });

    this.updateUnitOptionsForProduct(productId, this.budgetLineForm, 'budget');
    this.updateFilteredTaxes();

    // Deshabilitar unidad de medida solo cuando el producto tenga una única unidad posible.
    if (productId && productId !== 0) {
      this.updateUnitControlState(productId, this.budgetLineForm);
    }

    this.updateValidators();

    // Desactivar bandera después de la inicialización
    this.isInitializingForm = false;
    this.updateFilteredTaxes();
  }

  getLineLabel(line: any): string {
    return line.name || 'Sin nombre';
  }

  getAssociatedSaleOptionId(line: any): number | null {
    return this.getBudgetLineId(line);
  }

  updateAssociatedSaleLines(): void {
    const currentLineId = this.getBudgetLineId(this.nzData.lineData);

    this.associatedSaleLines = (this.nzData.budgetLines || []).filter(
      (line) => {
        const lineId = this.getBudgetLineId(line);
        const isSelf = currentLineId && lineId === currentLineId;
        const isCost = line.isCost === true;
        return !isSelf && !isCost && !!lineId;
      },
    );

    const associatedCtrl = this.budgetLineForm.get('associatedSaleLineId');
    const selectedAssociatedSaleId = this.getIdValue(associatedCtrl?.value);
    if (selectedAssociatedSaleId) {
      associatedCtrl?.setValue(selectedAssociatedSaleId, { emitEvent: false });
    }
  }

  updateValidators(): void {
    const isCost = this.budgetLineForm.get('isCost')?.value;
    const associatedCtrl = this.budgetLineForm.get('associatedSaleLineId');
    const supplierCtrl = this.budgetLineForm.get('supplierId');

    if (isCost) {
      if (this.createAssociatedSale) {
        associatedCtrl?.clearValidators();
        associatedCtrl?.setValue(null);
        associatedCtrl?.disable();
      } else {
        associatedCtrl?.setValidators([Validators.required]);
        associatedCtrl?.enable();
      }
      supplierCtrl?.enable();
    } else {
      associatedCtrl?.clearValidators();
      associatedCtrl?.disable();
      associatedCtrl?.setValue(null);
      supplierCtrl?.enable();
    }
    associatedCtrl?.updateValueAndValidity();
    supplierCtrl?.updateValueAndValidity();
    this.syncCostCurrencyFromAssociatedSale();
  }

  private markFormDirty(form: FormGroup): void {
    Object.values(form.controls).forEach((control) => {
      if (control.invalid) {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      }
    });
  }

  private withSelectedProduct(products: Product[], selectedProductId: any): Product[] {
    const normalizedSelectedProductId = this.getIdValue(selectedProductId);
    if (!normalizedSelectedProductId) {
      return products;
    }

    const hasSelectedProduct = products.some(
      (product) => this.getIdValue(product.id) === normalizedSelectedProductId,
    );
    if (hasSelectedProduct) {
      return products;
    }

    const selectedProduct = this.productList.find(
      (product) => this.getIdValue(product.id) === normalizedSelectedProductId,
    );

    return selectedProduct ? [selectedProduct, ...products] : products;
  }

  loadReferenceData(): void {
    const saleOrderId = this.getSaleOrderId();
    if (saleOrderId) {
      this.loadSaleOrderLineOptions(saleOrderId);
    }

    // Cargar proveedores
    this.globalValuesService.isPartnerReady$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isReady) => {
        if (isReady) {
          const partnerList = this.globalValuesService.partnerlist || [];
          // Suppliers son partners con partnerTypeId === 6 o 7 (providers)
          this.supplierList =
            partnerList.filter(
              (p: any) => p.partnerTypeId === 6 || p.partnerTypeId === 7,
            ) || [];
        }
      });

    // También obtener el valor actual si ya está listo
    if (this.globalValuesService.isPartnerReady$.value) {
      const partnerList = this.globalValuesService.partnerlist || [];
      this.supplierList =
        partnerList.filter(
          (p: any) => p.partnerTypeId === 6 || p.partnerTypeId === 7,
        ) || [];
    }

    // Cargar productos filtrados por la compañía actual y tipo (venta/coste)
    const isCostControl = this.budgetLineForm.get('isCost');
    const isCost$ = isCostControl
      ? isCostControl.valueChanges.pipe(startWith(isCostControl.value))
      : of(false);

    combineLatest([
      this.globalValuesService.products$,
      this.companyService.selectedCompanyId$,
      isCost$
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([products, companyId, isCost]) => {
        if (this.lineOptions) {
          return;
        }

        if (companyId) {
          // Filtrar productos por la compañía actualmente seleccionada. Un producto global
          // (companyId null) es válido para cualquier compañía, igual que en el servidor.
          const companyProducts = (products || []).filter((product: any) =>
            isProductInCompanyScope(product, companyId)
          );

          // Filtrar por tipo según isCost
          this.filteredProducts = companyProducts.filter((product: any) => {
            if (isCost) {
              return product.purchaseOk === true;
            } else {
              return product.saleOk === true;
            }
          });

          this.productList = companyProducts; // Mantener lista completa para otros usos
          this.costFilteredProducts = companyProducts.filter((product: any) => product.purchaseOk === true);
          this.saleFilteredProducts = companyProducts.filter((product: any) => product.saleOk === true);
          this.refreshAllUnitOptions();
          this.updateFilteredTaxes();
          this.updateFilteredCostTaxes();
        } else {
          this.productList = [];
          this.filteredProducts = [];
          this.costFilteredProducts = [];
          this.saleFilteredProducts = [];
          this.refreshAllUnitOptions();
          this.filteredTaxes = [];
          this.costFilteredTaxes = [];
        }
        this.updateAssociatedSaleLines();
      });

    // Cargar unidades de medida
    this.globalValuesService.unitOfMeasures$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((units) => {
        if (this.lineOptions) {
          return;
        }
        this.unitList = units || [];
        this.refreshAllUnitOptions();
      });

    // También obtener el valor actual si ya está listo
    if (this.globalValuesService.isUnitOfMeasureReady$.value) {
      if (!this.lineOptions) {
        this.unitList = this.globalValuesService.unitOfMeasureslist || [];
        this.refreshAllUnitOptions();
      }
    }

    // Cargar monedas
    this.globalValuesService.currencies$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((currencies) => {
        this.currencies = currencies || [];
      });

    // También obtener el valor actual si ya está listo
    if (this.globalValuesService.currencies$.value.length > 0) {
      this.currencies = this.globalValuesService.currencies$.value || [];
    }

    // Cargar localizaciones
    this.globalValuesService.places$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((locations: any[]) => {
        this.locationList = locations || [];
      });

    // Cargar taxes de la compañía
    combineLatest([
      this.globalValuesService.companyTaxes$,
      this.companyService.selectedCompanyId$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([taxes, companyId]) => {
        this.taxCatalog = taxes || [];
        if (this.lineOptions) {
          this.updateFilteredTaxes();
          this.updateFilteredCostTaxes();
          return;
        }
        // CEC-689 · opción C: la lista provisional (hasta que llegan las line-options) es la de la compañía de la
        // cotización, no la del conmutador.
        const quoteCompanyId = this.nzData.budget?.companyId ?? companyId;
        if (quoteCompanyId) {
          this.companyTaxes = (taxes || []).filter(
            (tax: any) => tax.companyId === quoteCompanyId,
          );
          this.updateFilteredTaxes();
          this.updateFilteredCostTaxes();
        } else {
          this.companyTaxes = [];
          this.filteredTaxes = [];
          this.costFilteredTaxes = [];
        }
      });
  }

  private loadSaleOrderLineOptions(saleOrderId: number): void {
    this.opportunityService
      .getSaleOrderLineOptions(saleOrderId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const options: SaleOrderLineOptions = response?.data || response;
          this.lineOptions = options;

          const products = options.products || [];
          this.productList = products as Product[];
          this.filteredProducts = products.filter((product: any) =>
            this.budgetLineForm.get('isCost')?.value
              ? product.purchaseOk !== false
              : product.saleOk !== false
          ) as Product[];
          this.costFilteredProducts = products.filter((product: any) => product.purchaseOk !== false) as Product[];
          this.saleFilteredProducts = products.filter((product: any) => product.saleOk !== false) as Product[];
          this.unitList = options.unitsOfMeasure || [];
          this.companyTaxes = options.taxes || [];

          this.refreshAllUnitOptions();
          this.updateFilteredTaxes();
          this.updateFilteredCostTaxes();
          this.updateAssociatedSaleLines();
        },
        error: (error) => {
          console.error('Error loading sale order line options', error);
          this.showToast(
            error?.error?.message || 'No se pudieron cargar las opciones de línea de la cotización.',
            'error'
          );
        },
      });
  }

  onSupplierCreated(partner: Partner): void {
    if (!partner?.id) {
      return;
    }

    if (!this.supplierList.some((supplier) => Number(supplier.id) === Number(partner.id))) {
      this.supplierList = [...this.supplierList, partner];
    }

    this.budgetLineForm.patchValue({ supplierId: partner.id });
    this.costLineForm.patchValue({ supplierId: partner.id });
    this.saleLineForm.patchValue({ supplierId: partner.id });
  }

  onProductChange(productId: number | null): void {
    if (!productId || productId === 0) {
      this.budgetUnitList = this.unitList;
      this.budgetLineForm.get('unitOfMeasureId')?.enable();
      return;
    }

    const selectedProduct = this.productList.find((p) => p.id === productId);
    if (!selectedProduct) {
      return;
    }

    // Autocompletar campos con los datos del producto
    const updates: any = {};

    // Nombre / Descripción - NO autocompletar si estamos inicializando con datos existentes
    // Esto preserva las descripciones personalizadas cuando se abre para editar
    const productDescription = this.getProductLineDescription(selectedProduct);
    if (productDescription && !this.isInitializingForm) {
      updates.name = productDescription;
    }

    const isCost = this.budgetLineForm.get('isCost')?.value;

    // Precio unitario
    const productPrice = isCost ? selectedProduct.costPrice : selectedProduct.unitPrice;
    if (productPrice !== undefined && productPrice !== null) {
      updates.price = productPrice;
    }

    this.updateUnitOptionsForProduct(productId, this.budgetLineForm, 'budget');
    const productUnitIds = this.getProductUnitIds(selectedProduct);
    const defaultUnitId = this.getDefaultProductUnitId(selectedProduct);
    if (defaultUnitId) {
      updates.unitOfMeasureId = defaultUnitId;
    } else if (
      productUnitIds.length > 1 &&
      !productUnitIds.includes(Number(this.budgetLineForm.get('unitOfMeasureId')?.value))
    ) {
      updates.unitOfMeasureId = 0;
    }
    this.updateUnitControlState(productId, this.budgetLineForm);

    if (!this.budgetLineForm.get('currencyId')?.value) {
      updates.currencyId = this.getBudgetCurrencyId();
    }

    // Aplicar los cambios al formulario
    if (Object.keys(updates).length > 0) {
      this.budgetLineForm.patchValue(updates, { emitEvent: false });
      // Recalcular el importe después de actualizar el precio
      if (updates.price !== undefined) {
        this.calculateAmount();
      }
    }

    this.updateFilteredTaxes();
    this.budgetLineForm.patchValue({ taxIds: null }, { emitEvent: false });
  }

  onCostProductChange(productId: number | null): void {
    this.autoPrefilledCostProductId = null;

    if (!productId || productId === 0) {
      this.costUnitList = this.unitList;
      this.costLineForm.get('unitOfMeasureId')?.enable();
      this.costFilteredTaxes = [];
      this.costLineForm.patchValue({ taxIds: null }, { emitEvent: false });
      return;
    }

    const selectedProduct = this.productList.find((p) => p.id === productId);
    if (!selectedProduct) {
      return;
    }

    const updates: any = {};

    const productDescription = this.getProductLineDescription(selectedProduct);
    if (productDescription) {
      updates.name = productDescription;
    }

    if (selectedProduct.costPrice !== undefined && selectedProduct.costPrice !== null) {
      updates.price = selectedProduct.costPrice;
    }

    this.updateUnitOptionsForProduct(productId, this.costLineForm, 'cost');
    const productUnitIds = this.getProductUnitIds(selectedProduct);
    const defaultUnitId = this.getDefaultProductUnitId(selectedProduct);
    if (defaultUnitId) {
      updates.unitOfMeasureId = defaultUnitId;
    } else if (
      productUnitIds.length > 1 &&
      !productUnitIds.includes(Number(this.costLineForm.get('unitOfMeasureId')?.value))
    ) {
      updates.unitOfMeasureId = 0;
    }
    this.updateUnitControlState(productId, this.costLineForm);

    if (Object.keys(updates).length > 0) {
      this.costLineForm.patchValue(updates, { emitEvent: false });
      this.calculateCostAmount();
    }

    this.updateFilteredCostTaxes();
  }

  private prefillCostFromSale(): void {
    const saleValue = this.budgetLineForm.getRawValue();
    const currentCostValue = this.costLineForm.getRawValue();
    const saleProductId = this.getIdValue(saleValue.productId);
    const currentCostProductId = this.getIdValue(currentCostValue.productId);
    const shouldUseSaleProduct =
      !!saleProductId &&
      (!currentCostProductId || currentCostProductId === this.autoPrefilledCostProductId);
    const costProductId = shouldUseSaleProduct ? saleProductId : currentCostProductId;
    const selectedCostProduct = this.productList.find(
      (product) => this.getIdValue(product.id) === costProductId,
    );

    this.costLineForm.patchValue({
      name: currentCostValue.name || saleValue.name || '',
      isCost: true,
      isUnitPrice: currentCostValue.isUnitPrice ?? saleValue.isUnitPrice ?? true,
      productId: costProductId,
      supplierId: currentCostValue.supplierId || saleValue.supplierId || 0,
      quantity: currentCostValue.quantity || saleValue.quantity || 0,
      unitOfMeasureId:
        currentCostValue.unitOfMeasureId ||
        this.getDefaultProductUnitId(selectedCostProduct) ||
        saleValue.unitOfMeasureId ||
        0,
      price:
        currentCostValue.price ||
        selectedCostProduct?.costPrice ||
        0,
      discount: currentCostValue.discount || 0,
      currencyId: saleValue.currencyId || currentCostValue.currencyId || this.nzData.budget?.currencyId || 1,
      taxIds: currentCostValue.taxIds || null,
      associatedSaleLineId: null,
      sourceId: currentCostValue.sourceId || saleValue.sourceId || null,
      destinationId: currentCostValue.destinationId || saleValue.destinationId || null,
    }, { emitEvent: false });

    this.updateUnitOptionsForProduct(costProductId, this.costLineForm, 'cost');
    this.updateUnitControlState(costProductId, this.costLineForm);

    this.autoPrefilledCostProductId = shouldUseSaleProduct ? costProductId : null;
    this.calculateCostAmount();
    this.updateFilteredCostTaxes();
  }

  onAssociatedSaleProductChange(productId: number | null): void {
    this.autoPrefilledSaleProductId = null;

    if (!productId || productId === 0) {
      this.saleUnitList = this.unitList;
      this.saleLineForm.get('unitOfMeasureId')?.enable();
      return;
    }

    const selectedProduct = this.productList.find((p) => p.id === productId);
    if (!selectedProduct) {
      return;
    }

    const updates: any = {};

    const productDescription = this.getProductLineDescription(selectedProduct);
    if (productDescription) {
      updates.name = productDescription;
    }

    if (selectedProduct.unitPrice !== undefined && selectedProduct.unitPrice !== null) {
      updates.price = selectedProduct.unitPrice;
    }

    this.updateUnitOptionsForProduct(productId, this.saleLineForm, 'sale');
    const productUnitIds = this.getProductUnitIds(selectedProduct);
    const defaultUnitId = this.getDefaultProductUnitId(selectedProduct);
    if (defaultUnitId) {
      updates.unitOfMeasureId = defaultUnitId;
    } else if (
      productUnitIds.length > 1 &&
      !productUnitIds.includes(Number(this.saleLineForm.get('unitOfMeasureId')?.value))
    ) {
      updates.unitOfMeasureId = 0;
    }
    this.updateUnitControlState(productId, this.saleLineForm);

    if (!this.saleLineForm.get('currencyId')?.value) {
      updates.currencyId = this.getBudgetCurrencyId();
    }

    if (Object.keys(updates).length > 0) {
      this.saleLineForm.patchValue(updates, { emitEvent: false });
      this.calculateSaleAmount();
    }
  }

  private prefillSaleFromCost(): void {
    const costValue = this.budgetLineForm.getRawValue();
    const currentSaleValue = this.saleLineForm.getRawValue();
    const costProductId = this.getIdValue(costValue.productId);
    const currentSaleProductId = this.getIdValue(currentSaleValue.productId);
    const shouldUseCostProduct =
      !!costProductId &&
      (!currentSaleProductId || currentSaleProductId === this.autoPrefilledSaleProductId);
    const saleProductId = shouldUseCostProduct ? costProductId : currentSaleProductId;
    const selectedSaleProduct = this.productList.find(
      (product) => this.getIdValue(product.id) === saleProductId,
    );

    this.saleLineForm.patchValue({
      name: currentSaleValue.name || costValue.name || '',
      isCost: false,
      isUnitPrice: currentSaleValue.isUnitPrice ?? costValue.isUnitPrice ?? true,
      productId: saleProductId,
      supplierId: 0,
      quantity: currentSaleValue.quantity || costValue.quantity || 0,
      unitOfMeasureId:
        currentSaleValue.unitOfMeasureId ||
        this.getDefaultProductUnitId(selectedSaleProduct) ||
        costValue.unitOfMeasureId ||
        0,
      price:
        currentSaleValue.price ||
        selectedSaleProduct?.unitPrice ||
        0,
      discount: currentSaleValue.discount || 0,
      currencyId: currentSaleValue.currencyId || this.getBudgetCurrencyId(),
      taxIds: null,
      associatedSaleLineId: null,
      sourceId: currentSaleValue.sourceId || costValue.sourceId || null,
      destinationId: currentSaleValue.destinationId || costValue.destinationId || null,
    }, { emitEvent: false });

    this.updateUnitOptionsForProduct(saleProductId, this.saleLineForm, 'sale');
    this.updateUnitControlState(saleProductId, this.saleLineForm);

    this.autoPrefilledSaleProductId = shouldUseCostProduct ? saleProductId : null;
    this.calculateSaleAmount();
  }

  private getProductUnitIds(product: any): number[] {
    const ids = [
      ...(Array.isArray(product?.suggestedUnitOfMeasureIds) ? product.suggestedUnitOfMeasureIds : []),
      ...(Array.isArray(product?.unitOfMeasureIds) ? product.unitOfMeasureIds : []),
      ...(Array.isArray(product?.unitOfMeasures)
        ? product.unitOfMeasures.map((unit: any) => unit?.id)
        : []),
      product?.unitOfMeasureId,
      product?.unitOfMeasure?.id,
    ];

    return [...new Set(ids.map((id) => Number(id)).filter((id) => id > 0))];
  }

  private getDefaultProductUnitId(product: any): number {
    const unitIds = this.getProductUnitIds(product);
    const defaultUnitId = Number(product?.defaultUnitOfMeasureId || product?.unitOfMeasureId || product?.unitOfMeasure?.id || 0);
    if (defaultUnitId && unitIds.includes(defaultUnitId)) {
      return defaultUnitId;
    }

    return unitIds.length === 1 ? unitIds[0] : 0;
  }

  private getProductLineDescription(product: Product | any): string {
    return product?.description || product?.name || '';
  }

  getSelectedProductUnitCategoryId(target: 'budget' | 'cost' | 'sale'): number | null {
    const form =
      target === 'budget'
        ? this.budgetLineForm
        : target === 'cost'
          ? this.costLineForm
          : this.saleLineForm;
    const productId = Number(form.get('productId')?.value || 0);
    const product = this.productList.find((p) => Number(p.id) === productId);
    const defaultUnitId = this.getDefaultProductUnitId(product) || this.getProductUnitIds(product)[0];
    const defaultUnit = this.unitList.find((unit) => Number(unit.id) === defaultUnitId);

    return Number(defaultUnit?.unitOfMeasureCategoryId || 0) || null;
  }

  private getUnitsForProduct(_productId: number | null): any[] {
    return this.unitList;
  }

  private updateUnitOptionsForProduct(
    productId: number | null,
    form: FormGroup,
    target: 'budget' | 'cost' | 'sale',
  ): void {
    const units = this.getUnitsForProduct(productId);
    if (target === 'budget') {
      this.budgetUnitList = units;
    } else if (target === 'cost') {
      this.costUnitList = units;
    } else {
      this.saleUnitList = units;
    }

    this.mergeSelectedUnit(form, target);
  }

  private updateUnitControlState(productId: number | null, form: FormGroup): void {
    const unitControl = form.get('unitOfMeasureId');
    unitControl?.enable();
  }

  onUnitOfMeasureCreated(unit: any, target: 'budget' | 'cost' | 'sale'): void {
    const form =
      target === 'budget'
        ? this.budgetLineForm
        : target === 'cost'
          ? this.costLineForm
          : this.saleLineForm;
    const productId = Number(form.get('productId')?.value || 0);
    this.associateUnitToProduct(productId, unit, form, target);
  }

  private associateUnitToProduct(
    productId: number,
    unit: any,
    form: FormGroup,
    target: 'budget' | 'cost' | 'sale',
  ): void {
    const unitId = Number(unit?.id || 0);
    const product = this.productList.find((p) => Number(p.id) === productId);
    if (!product || !unitId) {
      return;
    }

    const unitIds = this.getProductUnitIds(product);
    if (unitIds.includes(unitId)) {
      this.updateUnitOptionsForProduct(productId, form, target);
      return;
    }

    const defaultUnit = this.unitList.find((u) => Number(u.id) === this.getDefaultProductUnitId(product));
    const defaultCategoryId = Number(defaultUnit?.unitOfMeasureCategoryId || 0);
    const newCategoryId = Number(unit?.unitOfMeasureCategoryId || 0);
    if (defaultCategoryId && newCategoryId && defaultCategoryId !== newCategoryId) {
      this.showToast(
        'La unidad creada pertenece a otra categoria y no se puede asociar al producto.',
        'warning'
      );
      form.patchValue({ unitOfMeasureId: this.getDefaultProductUnitId(product) || 0 }, { emitEvent: false });
      this.updateUnitOptionsForProduct(productId, form, target);
      return;
    }

    const updatedUnitIds = [...unitIds, unitId];
    const payload = this.buildProductUnitUpdatePayload(product, updatedUnitIds, unitId);

    this.productsService.updateProducts(payload).subscribe({
      next: () => {
        product.unitOfMeasureIds = updatedUnitIds;
        product.unitOfMeasures = [
          ...(product.unitOfMeasures || []),
          unit,
        ].filter((item, index, list) => list.findIndex((u) => Number(u.id) === Number(item.id)) === index);
        this.globalValuesService.products$.next([...this.productList]);
        this.updateUnitOptionsForProduct(productId, form, target);
      },
      error: (error) => {
        console.error('Error associating unit of measure to product', error);
        this.showToast(
          error?.error?.message || 'La unidad fue creada, pero no se pudo asociar al producto.',
          'error'
        );
        form.patchValue({ unitOfMeasureId: this.getDefaultProductUnitId(product) || 0 }, { emitEvent: false });
        this.updateUnitOptionsForProduct(productId, form, target);
      },
    });
  }

  private buildProductUnitUpdatePayload(product: Product, unitOfMeasureIds: number[], fallbackUnitId: number): Product {
    return {
      id: product.id,
      name: product.name,
      defaultCode: product.defaultCode,
      description: product.description,
      productType: product.productType || 'Service',
      unitPrice: product.unitPrice,
      costPrice: product.costPrice,
      saleOk: product.saleOk ?? true,
      purchaseOk: product.purchaseOk ?? true,
      isActive: product.isActive ?? true,
      companyId: product.companyId,
      unitOfMeasureId: this.getDefaultProductUnitId(product) || fallbackUnitId,
      unitOfMeasureIds,
      taxIds: product.taxes?.map((tax) => tax.id),
      supplierTaxIds: product.supplierTaxes?.map((tax) => tax.id),
    } as Product;
  }

  private showToast(content: string, type: 'success' | 'warning' | 'error' = 'success', msg?: string): void {
    const configByType = {
      success: { icon: 'check_circle', iconColor: '#6CDA75' },
      warning: { icon: 'warning', iconColor: '#E5A100' },
      error: { icon: 'dangerous', iconColor: '#CB2A2F' },
    };

    this.toastConfig = {
      ...configByType[type],
      content,
      msg,
      showUndo: false,
    };

    if (!this.notificationTmpl) {
      return;
    }

    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      {
        nzPlacement: 'bottomRight',
        nzClass: 'custom-notification',
        // CEC-687: un fallo no se desvanece solo; se queda hasta cerrarlo con la X. Los demás, como siempre.
        ...(type === 'error' ? { nzDuration: 0 } : {}),
      }
    );
  }

  private refreshAllUnitOptions(): void {
    this.updateUnitOptionsForProduct(this.budgetLineForm.get('productId')?.value, this.budgetLineForm, 'budget');
    this.updateUnitOptionsForProduct(this.costLineForm.get('productId')?.value, this.costLineForm, 'cost');
    this.updateUnitOptionsForProduct(this.saleLineForm.get('productId')?.value, this.saleLineForm, 'sale');
  }

  private mergeSelectedUnit(form: FormGroup, target: 'budget' | 'cost' | 'sale'): void {
    const currentUnitId = Number(form.get('unitOfMeasureId')?.value || 0);
    if (!currentUnitId) {
      return;
    }

    const hasUnit = this.unitList.some((unit) => Number(unit.id) === currentUnitId);
    if (hasUnit) {
      return;
    }

    const source =
      target === 'budget'
        ? this.nzData.lineData
        : target === 'cost'
          ? this.costLineForm.getRawValue()
          : this.saleLineForm.getRawValue();
    const unit = source?.unitOfMeasure || source?.unitMeasure;
    if (!unit?.id) {
      return;
    }

    this.unitList = [...this.unitList, unit];
  }

  updateFilteredTaxes(): void {
    const productId = this.budgetLineForm.get('productId')?.value;

    if (!productId || productId === 0) {
      this.filteredTaxes = [];
      // Limpiar el tax seleccionado si no hay producto
      this.budgetLineForm.patchValue({ taxIds: null }, { emitEvent: false });
      return;
    }

    // CEC-687: nunca se descarta el impuesto que ya tiene la línea, aunque su producto esté archivado
    // o el impuesto sea de otra compañía. Vaciarlo hacía que al guardar se enviara taxIds: [] y el
    // servidor lo borrara. Ahora se enseña y se envía tal cual: si no vale, el servidor lo rechaza y se avisa.
    this.filteredTaxes = resolveOfferedTaxes(this.companyTaxes, this.budgetLineForm.get('taxIds')?.value, this.taxCatalog);
  }

  updateFilteredCostTaxes(): void {
    const productId = this.costLineForm.get('productId')?.value;

    if (!productId || productId === 0) {
      this.costFilteredTaxes = [];
      this.costLineForm.patchValue({ taxIds: null }, { emitEvent: false });
      return;
    }

    // CEC-687: misma regla que en la línea de venta.
    this.costFilteredTaxes = resolveOfferedTaxes(this.companyTaxes, this.costLineForm.get('taxIds')?.value, this.taxCatalog);
  }

  calculateAmount(): void {
    const quantity = this.budgetLineForm.get('quantity')?.value || 0;
    const price = this.budgetLineForm.get('price')?.value || 0;
    const discount = this.budgetLineForm.get('discount')?.value || 0;
    // El importe siempre se calcula con la cantidad estimada.
    const subtotal = quantity * price;
    const discountAmount = subtotal * (discount / 100);
    const amount = subtotal - discountAmount;

    this.budgetLineForm.patchValue({ amount }, { emitEvent: false });
  }

  calculateCostAmount(): void {
    const quantity = this.costLineForm.get('quantity')?.value || 0;
    const price = this.costLineForm.get('price')?.value || 0;
    const discount = this.costLineForm.get('discount')?.value || 0;
    const subtotal = quantity * price;
    const discountAmount = subtotal * (discount / 100);
    const amount = subtotal - discountAmount;

    this.costLineForm.patchValue({ amount }, { emitEvent: false });
  }

  calculateSaleAmount(): void {
    const quantity = this.saleLineForm.get('quantity')?.value || 0;
    const price = this.saleLineForm.get('price')?.value || 0;
    const discount = this.saleLineForm.get('discount')?.value || 0;
    const subtotal = quantity * price;
    const discountAmount = subtotal * (discount / 100);
    const amount = subtotal - discountAmount;

    this.saleLineForm.patchValue({ amount }, { emitEvent: false });
  }

  getCurrencyCode(): string {
    const currencyId = this.budgetLineForm.get('currencyId')?.value || this.getBudgetCurrencyId();
    const currency = this.currencies.find((c) => c.id === currencyId);
    return currency?.isoCode || currency?.code || 'USD';
  }

  getCostCurrencyCode(): string {
    const currencyId =
      this.costLineForm.get('currencyId')?.value ||
      this.getBudgetCurrencyId();
    const currency = this.currencies.find((c) => c.id === currencyId);
    return currency?.isoCode || currency?.code || 'USD';
  }

  getSaleCurrencyCode(): string {
    const currencyId =
      this.saleLineForm.get('currencyId')?.value ||
      this.getBudgetCurrencyId();
    const currency = this.currencies.find((c) => c.id === currencyId);
    return currency?.isoCode || currency?.code || 'USD';
  }

  private getBudgetCurrencyId(): number {
    return this.nzData.budget?.currencyId || 1;
  }

  private getLineCurrencyId(line: any): number | null {
    return this.getIdValue(line?.currencyId) || this.getIdValue(line?.currency?.id) || this.getIdValue(line?.currency);
  }

  private getAssociatedSaleLineCurrencyId(associatedSaleLineId: any): number {
    const associatedId = this.getIdValue(associatedSaleLineId);
    const associatedLine = this.associatedSaleLines.find((line) => this.getBudgetLineId(line) === associatedId);
    return this.getLineCurrencyId(associatedLine) || this.getBudgetCurrencyId();
  }

  private syncCostCurrencyFromAssociatedSale(): void {
    const isCost = this.budgetLineForm.get('isCost')?.value;
    const associatedSaleLineId = this.budgetLineForm.get('associatedSaleLineId')?.value;

    if (!isCost || this.createAssociatedSale || !associatedSaleLineId) {
      return;
    }

    const currencyId = this.getAssociatedSaleLineCurrencyId(associatedSaleLineId);
    this.budgetLineForm.patchValue({ currencyId }, { emitEvent: false });
    this.calculateAmount();
  }

  close(): void {
    this.drawerRef.close();
  }

  discard(): void {
    this.close();
  }

  saveAndNew(): void {
    this.saveLine(true);
  }

  saveAndClose(): void {
    this.saveLine(false);
  }

  private getNormalizedLineData(response: any): any {
    const resultData = response?.data || response;
    return Array.isArray(resultData) ? resultData[0] : resultData;
  }

  private getIdValue(value: any): number | null {
    if (value === null || value === undefined || value === '' || value === 0) {
      return null;
    }

    const rawId = value && typeof value === 'object' ? (value.id ?? value.value) : value;
    const numericId = Number(rawId);

    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  }

  private getBudgetLineId(line: any): number | null {
    if (!line) {
      return null;
    }

    return (
      this.getIdValue(line.id) ||
      this.getIdValue(line.budgetLineId) ||
      this.getIdValue(line.budgetLine) ||
      this.getIdValue(line.originalId)
    );
  }

  private createLocalBudgetLineId(): number {
    CreateBudgetLineDrawerComponent.localBudgetLineIdSequence += 1;
    return Date.now() + CreateBudgetLineDrawerComponent.localBudgetLineIdSequence;
  }

  private buildLocalBudgetLine(lineData: any): any {
    const lineId = this.getBudgetLineId(lineData) || this.createLocalBudgetLineId();
    return {
      ...lineData,
      id: lineId,
      localId: lineId,
    };
  }

  private mergeSavedLineWithPayload(lineData: any, payload: any): any {
    const normalizedLineData = lineData?.data || lineData || {};
    const mergedLineData = {
      ...payload,
      ...normalizedLineData,
    };
    const lineId = this.getBudgetLineId(mergedLineData);
    if (lineId) {
      mergedLineData.id = lineId;
    }

    const associatedSaleLineId =
      this.getIdValue(mergedLineData.associatedSaleLineId) ||
      this.getIdValue(mergedLineData.associatedSale) ||
      this.getIdValue(mergedLineData.associatedSaleLine);
    if (associatedSaleLineId) {
      mergedLineData.associatedSaleLineId = associatedSaleLineId;
    }

    if (Object.prototype.hasOwnProperty.call(payload, 'supplierId')) {
      mergedLineData.supplierId = payload.supplierId;

      if (payload.supplierId === null) {
        mergedLineData.supplier = null;
      } else if (mergedLineData.supplier) {
        const responseSupplierId = typeof mergedLineData.supplier === 'object'
          ? mergedLineData.supplier.id
          : mergedLineData.supplier;

        if (Number(responseSupplierId) !== Number(payload.supplierId)) {
          mergedLineData.supplier = null;
        }
      }
    }

    if (payload.taxIds && (!mergedLineData.taxIds || mergedLineData.taxIds.length === 0)) {
      mergedLineData.taxIds = payload.taxIds;
    }

    return mergedLineData;
  }

  private resetPrimaryForm(): void {
    this.budgetLineForm.reset({
      name: '',
      isCost: false,
      isUnitPrice: true,
      productId: null,
      supplierId: 0,
      quantity: 0,
      unitOfMeasureId: 0,
      price: 0,
      discount: 0,
      currencyId: this.nzData.budget?.currencyId || 1,
      amount: 0,
      taxIds: null,
      associatedSaleLineId: null,
    });
    this.budgetUnitList = this.unitList;
    this.budgetLineForm.get('unitOfMeasureId')?.enable();
    this.updateValidators();
    this.calculateAmount();
    this.updateFilteredTaxes();
  }

  private resetCostForm(): void {
    this.costLineForm.reset({
      name: '',
      isCost: true,
      isUnitPrice: true,
      productId: null,
      supplierId: 0,
      quantity: 0,
      unitOfMeasureId: 0,
      price: 0,
      discount: 0,
      currencyId: this.nzData.budget?.currencyId || 1,
      amount: 0,
      taxIds: null,
      associatedSaleLineId: null,
      sourceId: null,
      destinationId: null,
    });
    this.costUnitList = this.unitList;
    this.costLineForm.get('unitOfMeasureId')?.enable();
    this.costLineForm.get('associatedSaleLineId')?.disable();
    this.autoPrefilledCostProductId = null;
    this.calculateCostAmount();
    this.costFilteredTaxes = [];
  }

  private resetSaleForm(): void {
    this.saleLineForm.reset({
      name: '',
      isCost: false,
      isUnitPrice: true,
      productId: null,
      supplierId: 0,
      quantity: 0,
      unitOfMeasureId: 0,
      price: 0,
      discount: 0,
      currencyId: this.nzData.budget?.currencyId || 1,
      amount: 0,
      taxIds: null,
      associatedSaleLineId: null,
      sourceId: null,
      destinationId: null,
    });
    this.saleUnitList = this.unitList;
    this.saleLineForm.get('unitOfMeasureId')?.enable();
    this.saleLineForm.get('associatedSaleLineId')?.disable();
    this.autoPrefilledSaleProductId = null;
    this.calculateSaleAmount();
  }

  private saveLine(createNew: boolean): void {
    if (this.saving) {
      return;
    }

    this.updateValidators();

    if (this.budgetLineForm.invalid) {
      this.markFormDirty(this.budgetLineForm);
      return;
    }

    if (this.showCostStep && this.costLineForm.invalid) {
      this.markFormDirty(this.costLineForm);
      this.currentStep = 'cost';
      return;
    }

    if (this.showSaleStep && this.saleLineForm.invalid) {
      this.markFormDirty(this.saleLineForm);
      this.currentStep = 'associatedSale';
      return;
    }

    const saleOrderId = this.nzData.budget?.id || this.nzData.budgetId;
    const formValue = this.budgetLineForm.getRawValue();

    // Helper para convertir IDs opcionales: si es 0 o no existe, retorna null
    const getIdValue = (value: any): number | null => this.getIdValue(value);

    const isCost = formValue.isCost !== undefined ? formValue.isCost : false;
    const isUnitPrice =
      formValue.isUnitPrice !== undefined ? formValue.isUnitPrice : true;
    const taxIds: number[] = formValue.taxIds ? [formValue.taxIds] : [];

    // Construir el payload según el formato del endpoint
    const payload: any = {
      saleOrderId: saleOrderId ? (typeof saleOrderId === 'string' ? parseInt(saleOrderId) : saleOrderId) : null,
      name: formValue.name || '',
      customerNote: this.nzData.lineData?.customerNote || '',
      quantity: formValue.quantity || 0,
      discount: formValue.discount || 0,
      isCost: isCost,
      price: formValue.price || 0,
      isUnitPrice: isUnitPrice,
      sequence: 0, // Se calculará en el servidor
      isActive: true,
      productId: getIdValue(formValue.productId),
      supplierId: getIdValue(formValue.supplierId),
      currencyId: isCost
        ? this.getAssociatedSaleLineCurrencyId(formValue.associatedSaleLineId)
        : (getIdValue(formValue.currencyId) || this.getBudgetCurrencyId()),
      unitOfMeasureId: getIdValue(formValue.unitOfMeasureId),
      associatedSaleLineId: isCost
        ? getIdValue(formValue.associatedSaleLineId)
        : null,
      taxIds: taxIds,
    };

    // Siempre incluir origen y destino (incluso si son null) especialmente para PUTs
    const sourceIdValue = getIdValue(formValue.sourceId);
    const destinationIdValue = getIdValue(formValue.destinationId);

    // Si estamos editando (PUT), siempre incluir estos campos
    if (this.isEditMode && this.nzData.lineData?.id) {
      payload.sourceId = sourceIdValue;
      payload.destinationId = destinationIdValue;
      payload.id = this.nzData.lineData.id;
    } else {
      // Para CREATE, solo incluir si tienen valores válidos
      if (sourceIdValue && sourceIdValue !== 0) {
        payload.sourceId = sourceIdValue;
      }
      if (destinationIdValue && destinationIdValue !== 0) {
        payload.destinationId = destinationIdValue;
      }
    }

    // Si no hay ID de presupuesto y no estamos editando (es una línea nueva para un presupuesto nuevo)
    // permitir guardar localmente sin llamar al backend
    const buildAssociatedCostPayload = (associatedSaleLineId: number): any => {
      const costValue = this.costLineForm.getRawValue();
      const costPayload: any = {
        saleOrderId: typeof saleOrderId === 'string' ? parseInt(saleOrderId) : saleOrderId,
        name: costValue.name || '',
        customerNote: '',
        quantity: costValue.quantity || 0,
        discount: costValue.discount || 0,
        isCost: true,
        price: costValue.price || 0,
        isUnitPrice:
          costValue.isUnitPrice !== undefined ? costValue.isUnitPrice : true,
        sequence: 0,
        isActive: true,
        productId: getIdValue(costValue.productId),
        supplierId: getIdValue(costValue.supplierId),
        currencyId: costValue.currencyId || this.nzData.budget?.currencyId || 1,
        unitOfMeasureId: getIdValue(costValue.unitOfMeasureId),
        associatedSaleLineId,
        taxIds: costValue.taxIds ? [costValue.taxIds] : [],
      };

      const costSourceId = getIdValue(costValue.sourceId);
      const costDestinationId = getIdValue(costValue.destinationId);

      if (costSourceId) {
        costPayload.sourceId = costSourceId;
      }
      if (costDestinationId) {
        costPayload.destinationId = costDestinationId;
      }

      return costPayload;
    };

    const buildAssociatedSalePayload = (): any => {
      const saleValue = this.saleLineForm.getRawValue();
      const salePayload: any = {
        saleOrderId: typeof saleOrderId === 'string' ? parseInt(saleOrderId) : saleOrderId,
        name: saleValue.name || '',
        quantity: saleValue.quantity || 0,
        discount: saleValue.discount || 0,
        isCost: false,
        price: saleValue.price || 0,
        isUnitPrice:
          saleValue.isUnitPrice !== undefined ? saleValue.isUnitPrice : true,
        sequence: 0,
        isActive: true,
        productId: getIdValue(saleValue.productId),
        supplierId: getIdValue(saleValue.supplierId),
        currencyId: getIdValue(saleValue.currencyId) || this.getBudgetCurrencyId(),
        unitOfMeasureId: getIdValue(saleValue.unitOfMeasureId),
        associatedSaleLineId: null,
        taxIds: saleValue.taxIds ? [saleValue.taxIds] : [],
      };

      const saleSourceId = getIdValue(saleValue.sourceId);
      const saleDestinationId = getIdValue(saleValue.destinationId);

      if (saleSourceId) {
        salePayload.sourceId = saleSourceId;
      }
      if (saleDestinationId) {
        salePayload.destinationId = saleDestinationId;
      }

      return salePayload;
    };

    const buildCostPayloadWithAssociatedSale = (associatedSaleLineId: number): any => ({
      ...payload,
      isCost: true,
      associatedSaleLineId,
      taxIds,
    });

    const associatedSalePayload = this.showSaleStep ? buildAssociatedSalePayload() : null;

    // Si no hay ID de presupuesto y no estamos editando, guardar localmente sin llamar al backend.
    // Esto permite crear Venta + Coste asociado mientras la cotizacion nueva aun no esta persistida.
    if (!saleOrderId && !this.isEditMode) {
      let lineData: any;

      if (this.showCostStep) {
        const saleLine = this.buildLocalBudgetLine(payload);
        const associatedCostPayload = buildAssociatedCostPayload(saleLine.id);
        const costLine = this.buildLocalBudgetLine(associatedCostPayload);
        lineData = { budgetLines: [saleLine, costLine] };
      } else if (this.showSaleStep && associatedSalePayload) {
        const saleLine = this.buildLocalBudgetLine(associatedSalePayload);
        const costPayload = buildCostPayloadWithAssociatedSale(saleLine.id);
        const costLine = this.buildLocalBudgetLine(costPayload);
        lineData = { budgetLines: [saleLine, costLine] };
      } else {
        lineData = this.buildLocalBudgetLine(payload);
      }

      if (createNew) {
        this.lineCreated.emit(lineData);
        this.resetPrimaryForm();
        this.resetCostForm();
        this.resetSaleForm();
        this.createAssociatedCost = false;
        this.createAssociatedSale = false;
        this.currentStep = 'sale';
      } else {
        this.drawerRef.close(lineData);
      }
      return;
    }

    if (!saleOrderId) {
      console.error('No se puede guardar: falta ID de presupuesto');
      return;
    }

    this.saving = true;

    const request$: Observable<any> = this.showCostStep
      ? (this.isEditMode && payload.id
        ? this.opportunityService.updateBudgetLine(payload.id, payload)
        : this.opportunityService.createBudgetLine(payload)).pipe(
        switchMap((saleResponse: any) => {
          const saleLine = this.mergeSavedLineWithPayload(
            this.getNormalizedLineData(saleResponse),
            payload,
          );
          const saleLineId = saleLine?.id;

          if (!saleLineId) {
            throw new Error('No se pudo crear el coste asociado porque la venta creada no devolvió ID.');
          }

          const associatedCostPayload = buildAssociatedCostPayload(saleLineId);

          return this.opportunityService.createBudgetLine(associatedCostPayload).pipe(
            map((costResponse: any) => ({
              budgetLines: [
                saleLine,
                this.mergeSavedLineWithPayload(
                  this.getNormalizedLineData(costResponse),
                  associatedCostPayload,
                ),
              ],
            })),
          );
        }),
      )
      : this.showSaleStep
        ? this.opportunityService.createBudgetLine(associatedSalePayload).pipe(
          switchMap((saleResponse: any) => {
            const saleLine = this.mergeSavedLineWithPayload(
              this.getNormalizedLineData(saleResponse),
              associatedSalePayload,
            );
            const saleLineId = saleLine?.id;

            if (!saleLineId) {
              throw new Error('No se pudo crear el coste porque la venta asociada no devolvió ID.');
            }

            const costPayload = buildCostPayloadWithAssociatedSale(saleLineId);

            const saveCostLine$ = this.isEditMode && costPayload.id
              ? this.opportunityService.updateBudgetLine(costPayload.id, costPayload)
              : this.opportunityService.createBudgetLine(costPayload);

            return saveCostLine$.pipe(
              map((costResponse: any) => ({
                budgetLines: [
                  saleLine,
                  this.mergeSavedLineWithPayload(
                    this.getNormalizedLineData(costResponse),
                    costPayload,
                  ),
                ],
              })),
            );
          }),
        )
      : this.isEditMode && this.nzData.lineData?.id
        ? this.opportunityService.updateBudgetLine(payload.id, payload)
        : this.opportunityService.createBudgetLine(payload);

    request$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.saving = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          // La respuesta de update puede devolver un array de líneas o un objeto simple
          const lineData = response?.budgetLines
            ? response
            : this.mergeSavedLineWithPayload(
              this.getNormalizedLineData(response),
              payload,
            );

          if (createNew) {
            // Si hay datos, emitir el evento para que el componente padre agregue la línea
            if (lineData) {
              this.lineCreated.emit(lineData);
            }

            // Resetear formulario para nueva línea
            this.resetPrimaryForm();
            this.resetCostForm();
            this.resetSaleForm();
            this.createAssociatedCost = false;
            this.createAssociatedSale = false;
            this.currentStep = 'sale';
            // Habilitar unidad de medida al resetear (se deshabilitará cuando se seleccione producto)
            this.budgetLineForm.get('unitOfMeasureId')?.enable();
            this.calculateAmount();
            this.updateFilteredTaxes();
          } else {
            // Cerrar drawer y devolver los datos de la respuesta del servidor
            // El componente padre agregará la línea a la tabla
            this.drawerRef.close(lineData);
          }
        },
        error: (error: any) => this.handleSaveError(error),
      });
  }

  /**
   * CEC-687. Mismo aviso que el drawer de «Presupuesto a cliente»: titular fijo y, debajo, el motivo del
   * servidor traducido. Antes el texto crudo iba de titular, con ids internos y nombres de campos. El
   * drawer se queda abierto para no perder lo escrito, y el original queda entero en la consola.
   */
  private handleSaveError(error: unknown): void {
    console.error(this.isEditMode ? 'Error updating budget line:' : 'Error creating budget line:', error);

    this.showToast(
      this.isEditMode ? 'No se pudo guardar el cambio en la línea' : 'No se pudo crear la línea',
      'error',
      resolveLineWriteErrorMessage(error, 'Inténtalo de nuevo o avisa a soporte.'),
    );
  }

  ngOnDestroy(): void {
    // Remover listener nativo si fue registrado
    this.drawerEscapeBlocker.unblock();
  }
}

